/**
 * Wizard step-validation plumbing — the WCAG 3.3.1 (Error Identification) +
 * 3.3.2 wiring shared by every wizard that composes `WizardShell`.
 *
 * A step's `validate()` may return either:
 *
 *   - the legacy flat `string[]` of messages (surfaced only as the summary
 *     `role="alert"` block, unchanged), or
 *   - a `WizardValidationResult` that *also* maps messages to the field ids
 *     they belong to (`fields: [{ field: 'protocolNumber', message: … }]`).
 *
 * When the richer shape is returned, `WizardShell` marks the matching control
 * `aria-invalid` + `aria-describedby` (via the shared Field aria registry) and
 * moves focus to the FIRST invalid field, so a screen-reader / keyboard user
 * lands on the offending input — not just an alert summary they must hunt
 * for. Everything here is pure (JSX-free) so the `node --test` sweep covers
 * the decision table without a DOM.
 */

/** A single field-scoped validation issue: the control's DOM id + the message. */
export interface WizardFieldError {
  /** DOM `id` of the offending control (matches the `id`/`htmlFor` in the step body). */
  field: string;
  message: string;
}

/**
 * Richer validate() return: a summary message list PLUS the per-field issues
 * that drive aria-invalid / describedby / focus. Either half may be omitted.
 */
export interface WizardValidationResult {
  /** Summary messages for the `role="alert"` block. */
  messages?: string[];
  /** Field-scoped issues used for per-control aria + focus-to-first-invalid. */
  fields?: WizardFieldError[];
}

/** What a step validator may return. `null`/empty means "valid, advance". */
export type WizardValidateReturn =
  | string[]
  | WizardValidationResult
  | null
  | undefined;

/** Fully-normalized outcome the shell consumes. */
export interface NormalizedWizardValidation {
  /** True when the step should be blocked from advancing. */
  hasErrors: boolean;
  /** Flat summary messages (order-preserving) for the alert block. */
  messages: string[];
  /** Field issues in declaration order. */
  fields: WizardFieldError[];
  /** `field` id → its messages, first-seen order preserved. */
  fieldMap: Record<string, string[]>;
}

function isValidationResult(
  value: string[] | WizardValidationResult,
): value is WizardValidationResult {
  return !Array.isArray(value);
}

/**
 * Normalize any `validate()` return into a single outcome the shell can act
 * on. A step counts as failed when it produced ANY summary message or ANY
 * field issue. Field messages that carry a `message` are also folded into the
 * summary list (so the alert block stays complete) without duplicating a
 * message already present.
 */
export function normalizeWizardValidation(
  value: WizardValidateReturn,
): NormalizedWizardValidation {
  const empty: NormalizedWizardValidation = {
    hasErrors: false,
    messages: [],
    fields: [],
    fieldMap: {},
  };
  if (value == null) return empty;

  let messages: string[] = [];
  let fields: WizardFieldError[] = [];

  if (Array.isArray(value)) {
    messages = value.filter((m) => m.length > 0);
  } else if (isValidationResult(value)) {
    messages = (value.messages ?? []).filter((m) => m.length > 0);
    fields = (value.fields ?? []).filter((f) => f.field.length > 0);
  }

  // Fold field messages into the summary list too (deduped), so the alert
  // block remains a complete summary even when a validator only returned
  // field-scoped issues.
  const summary: string[] = [...messages];
  const fieldMap: Record<string, string[]> = {};
  for (const f of fields) {
    const bucket = fieldMap[f.field] ?? (fieldMap[f.field] = []);
    bucket.push(f.message);
    if (f.message && !summary.includes(f.message)) summary.push(f.message);
  }

  return {
    hasErrors: summary.length > 0 || fields.length > 0,
    messages: summary,
    fields,
    fieldMap,
  };
}

/**
 * The DOM id of the first invalid field (declaration order), or `undefined`
 * when the outcome has no field issues. `WizardShell` uses this to move focus
 * to the offending control after a failed Next (WCAG 3.3.1 / 2.4.3).
 */
export function firstInvalidFieldId(
  outcome: NormalizedWizardValidation,
): string | undefined {
  return outcome.fields.at(0)?.field;
}

/**
 * Focus (and, when it is a labelled form control, select) the first invalid
 * field within a step-body container. Returns true when a control was found
 * and focused. Pure-ish DOM helper kept out of the component so the id
 * resolution is unit-testable; the actual focus call is guarded for SSR /
 * non-element roots.
 */
export function focusFirstInvalidField(
  root: { querySelector: (selectors: string) => Element | null } | null,
  outcome: NormalizedWizardValidation,
): boolean {
  if (!root) return false;
  const id = firstInvalidFieldId(outcome);
  if (!id) return false;
  // CSS.escape keeps ids with special chars (e.g. dotted keys) query-safe.
  const escaped =
    typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
      ? CSS.escape(id)
      : id.replace(/["\\]/g, '\\$&');
  const el = root.querySelector(`#${escaped}`);
  if (el && typeof (el as HTMLElement).focus === 'function') {
    (el as HTMLElement).focus();
    return true;
  }
  return false;
}
